import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHead, Btn, Card, Field, Toggle, Label } from "@/admin/ui";
import { toast } from "sonner";

const CraftsAdmin = () => {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [banner, setBanner] = useState({ crafts_banner_enabled: false, crafts_banner_text: "" });
  const [savingBanner, setSavingBanner] = useState(false);
  const [stats, setStats] = useState(null);
  const [shipOrders, setShipOrders] = useState(null);
  const [tracking, setTracking] = useState({});

  const load = () => api.get("/admin/crafts").then((r) => setItems(r.data)).catch(() => setItems([]));
  const loadShip = () => api.get("/admin/crafts-orders").then((r) => setShipOrders(r.data)).catch(() => setShipOrders([]));
  useEffect(() => {
    load();
    loadShip();
    api.get("/admin/crafts-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const s = r.data.settings || {};
      setBanner({ crafts_banner_enabled: !!s.crafts_banner_enabled, crafts_banner_text: s.crafts_banner_text || "" });
    }).catch(() => {});
  }, []);

  const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

  const markShipped = async (oid) => {
    try {
      await api.post(`/admin/crafts-orders/${oid}/ship`, { tracking: (tracking[oid] || "").trim() });
      toast.success("Marked as shipped — buyer notified by email");
      loadShip();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update order"); }
  };

  const saveBanner = async () => {
    setSavingBanner(true);
    try {
      await api.put("/admin/settings", banner);
      toast.success("Banner saved");
    } catch { toast.error("Could not save banner"); }
    setSavingBanner(false);
  };

  const create = async () => {
    try {
      const { data } = await api.post("/admin/crafts", { title: "Untitled craft" });
      nav(`/admin/crafts/${data.id}`);
    } catch { toast.error("Could not create"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/crafts-orders.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "crafts-orders.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export orders"); }
  };

  return (
    <div className="max-w-4xl">
      <PageHead title="Crafts (Digital)" sub="Your digital storefront at /crafts — sell downloads, art and courses. Delivered instantly after payment.">
        <a href="/crafts" target="_blank" rel="noreferrer" className="border border-espresso px-4 py-2.5 text-[0.68rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-cream transition-colors">View Crafts page</a>
        <Btn variant="ghost" onClick={exportCsv} testid="craft-export-csv">Export orders CSV</Btn>
        <Btn onClick={create} testid="craft-new">New craft</Btn>
      </PageHead>

      <div className="grid grid-cols-3 gap-4 mb-6" data-testid="crafts-stats">
        {[
          ["Revenue (GBP)", stats ? gbp(stats.revenue) : "—"],
          ["Orders", stats ? stats.orders : "—"],
          ["Units sold", stats ? stats.units : "—"],
        ].map(([label, val]) => (
          <div key={label} className="bg-cream border border-espresso/10 p-5">
            <p className="text-[0.6rem] tracking-[0.2em] uppercase text-espresso/50">{label}</p>
            <p className="font-serif text-3xl text-espresso mt-2 tabular-nums">{val}</p>
          </div>
        ))}
      </div>

      <div className="bg-cream border border-espresso/10 p-5 mb-6" data-testid="crafts-top">
        <p className="text-[0.62rem] tracking-[0.2em] uppercase text-espresso/60 mb-3">Top digital pieces</p>
        {!stats || stats.top.length === 0 ? (
          <p className="text-espresso/40 text-sm">No digital sales yet. Your best sellers will appear here.</p>
        ) : (
          <div className="divide-y divide-espresso/10">
            {stats.top.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <span className="text-espresso text-sm truncate"><span className="text-espresso/40 mr-2">{i + 1}.</span>{p.title}</span>
                <span className="text-espresso/60 text-sm tabular-nums shrink-0">{p.units} sold · {gbp(p.revenue)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {shipOrders && shipOrders.length > 0 && (
        <div className="bg-cream border border-espresso/10 p-5 mb-6" data-testid="crafts-ship-orders">
          <p className="text-[0.62rem] tracking-[0.2em] uppercase text-espresso/60 mb-3">Starter-kit orders to ship</p>
          <div className="divide-y divide-espresso/10">
            {shipOrders.map((o) => {
              const sh = o.shipping || {};
              const shipped = o.delivery_status === "shipped";
              const addr = [sh.name, sh.line1, sh.line2, sh.city, sh.state, sh.postal_code, sh.country].filter(Boolean).join(", ");
              return (
                <div key={o.id} data-testid={`crafts-ship-${o.id}`} className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-espresso text-sm font-medium">{o.order_number} · {gbp(o.total)}</p>
                      <p className="text-espresso/50 text-xs mt-0.5">{o.email}</p>
                      <p className="text-espresso/70 text-sm mt-1">{addr || "No address on file"}</p>
                      {sh.phone && <p className="text-espresso/50 text-xs">☏ {sh.phone}</p>}
                    </div>
                    {shipped && <span data-testid={`crafts-shipped-${o.id}`} className="text-[0.58rem] tracking-[0.14em] uppercase px-2 py-1 bg-sage/20 text-sage shrink-0">Shipped</span>}
                  </div>
                  {shipped ? (
                    o.tracking ? <p className="text-espresso/50 text-xs mt-2">Tracking: {o.tracking}</p> : null
                  ) : (
                    <div className="flex items-end gap-2 mt-3">
                      <div className="flex-1">
                        <Field label="Tracking (optional)" value={tracking[o.id] || ""}
                          onChange={(v) => setTracking((m) => ({ ...m, [o.id]: v }))} testid={`crafts-tracking-${o.id}`} />
                      </div>
                      <Btn onClick={() => markShipped(o.id)} testid={`crafts-ship-btn-${o.id}`}>Mark as shipped</Btn>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Card className="mb-6">
        <Toggle label="Promo banner on Crafts page" value={banner.crafts_banner_enabled}
          onChange={(v) => setBanner((b) => ({ ...b, crafts_banner_enabled: v }))} testid="crafts-banner-toggle" />
        <Field label="Banner text" value={banner.crafts_banner_text}
          onChange={(v) => setBanner((b) => ({ ...b, crafts_banner_text: v }))} testid="crafts-banner-text"
          placeholder="e.g. Use code ORI10 for 10% off — this week only" />
        <p className="text-xs text-espresso/40 -mt-2 mb-4">Shows a slim gold bar at the top of the /crafts storefront. Create the matching code in Discounts.</p>
        <Btn onClick={saveBanner} disabled={savingBanner} testid="crafts-banner-save">Save banner</Btn>
      </Card>

      {items === null ? <p className="text-espresso/40">Loading…</p> : items.length === 0 ? (
        <div className="bg-cream border border-espresso/10 p-10 text-center text-espresso/50">
          No digital pieces yet. Create your first one to start selling.
        </div>
      ) : (
        <>
          {(() => {
            const counts = items.reduce((m, c) => { m[c.slug] = (m[c.slug] || 0) + 1; return m; }, {});
            return Object.values(counts).some((n) => n > 1) ? (
              <div data-testid="crafts-dup-warning" className="bg-amber-50 border border-amber-300 text-amber-800 text-sm px-4 py-3 mb-4 rounded">
                ⚠ Duplicate products detected (same link). Open the extra one and delete it, or redeploy to auto-clean.
              </div>
            ) : null;
          })()}
          <div className="bg-cream border border-espresso/10 divide-y divide-espresso/10" data-testid="crafts-list">
            {items.map((c) => {
              const isDup = items.filter((x) => x.slug === c.slug).length > 1;
              return (
                <button key={c.id} onClick={() => nav(`/admin/crafts/${c.id}`)} data-testid={`craft-row-${c.id}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-ivory transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-14 bg-ivory border border-espresso/10 overflow-hidden shrink-0">
                      {c.cover_image ? <img src={c.cover_image.startsWith("http") ? c.cover_image : `${process.env.REACT_APP_BACKEND_URL}${c.cover_image}`} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0">
                      <p className="text-espresso font-medium truncate">{c.title}</p>
                      <p className="text-espresso/40 text-xs">/{c.slug} · {c.category} · {(c.assets || []).length} file(s)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {isDup && <span data-testid={`craft-dup-${c.id}`} className="text-[0.6rem] tracking-[0.14em] uppercase px-2 py-1 bg-amber-100 text-amber-700">Duplicate</span>}
                    <span className="text-espresso/70 text-sm tabular-nums">{c.price != null ? `£${Number(c.price).toFixed(2)}` : "No price"}</span>
                    <span className={`text-[0.6rem] tracking-[0.14em] uppercase px-2 py-1 ${c.status === "live" ? "bg-sage/20 text-sage" : "bg-espresso/10 text-espresso/50"}`}>{c.status}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default CraftsAdmin;
